import React from 'react';

interface GoalieSelectModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  players: any[];
  selectedPlayerNo: number | string | null;
  onSelectPlayerNo: (playerNo: number | string) => void;
  onConfirm: () => void;
  onClose: () => void;
  requireSelection?: boolean;
  submitting?: boolean;
}

const GoalieSelectModal: React.FC<GoalieSelectModalProps> = ({
  open,
  title,
  subtitle,
  players,
  selectedPlayerNo,
  onSelectPlayerNo,
  onConfirm,
  onClose,
  requireSelection = false,
  submitting = false,
}) => {
  if (!open) return null;

  const canConfirm = selectedPlayerNo !== null && selectedPlayerNo !== '' && !submitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-xl font-bold text-[#05396b]">{title}</h3>
            {subtitle ? <p className="mt-1 text-sm text-gray-600">{subtitle}</p> : null}
          </div>
          {!requireSelection && (
            <button
              type="button"
              className="text-2xl leading-none text-gray-400 hover:text-gray-600 disabled:opacity-50"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              &times;
            </button>
          )}
        </div>

        <div className="px-5 py-5">
          <label className="mb-2 block text-sm font-semibold text-[#05396b]">Goalie</label>
          <select
            value={selectedPlayerNo ?? ''}
            onChange={(e) => onSelectPlayerNo(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={submitting || players.length === 0}
            className="w-full rounded border border-[#22c55e] bg-[#eef5fc] px-4 py-3 text-base focus:outline-none disabled:opacity-60"
          >
            <option value="">Select goalie...</option>
            {players.map((player) => {
              const name = `${player.firstName || ''} ${player.lastName || ''}`.trim();
              return (
                <option key={player._id || player.no} value={player.no}>
                  #{player.no} {name}
                </option>
              );
            })}
          </select>
          {players.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">No goalies found on the game roster.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 rounded-b-lg">
          {!requireSelection && (
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg bg-gray-200 px-5 py-2.5 font-semibold text-gray-800 hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`rounded-lg px-5 py-2.5 font-semibold ${
              canConfirm
                ? 'bg-[#7fe526] text-black hover:bg-[#72d020]'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoalieSelectModal;
